import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    CalendarMonth,
    CheckCircle,
    Cancel,
    Refresh,
    Storage,
    TrendingUp,
    DataUsage,
    PlayArrow,
    Warning,
    Lock,
    Delete,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    currentMonth,
    dataProcess,
} from "../../../services/procedureService";

import {
    getDashboardDataStatus,
} from "../../../services/dashBoardService";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";
import SEO from "../../../utils/SEO";
import { monthNames } from "../../../component/constent";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// INPUT DATA CONFIGURATION
// ============================================================

const dataItems = [

    {
        key: "inputdata_immis",
        title: "Store Data",
        subtitle: "iMMIS",
        icon: Storage,
        required: true,
    },

    {
        key: "labour_timeoffice",
        title: "Time Office Data",
        subtitle: "Labour",
        icon: CalendarMonth,
        required: true,
    },

    {
        key: "labour_insentive",
        title: "Incentive Data",
        subtitle: "Labour",
        icon: TrendingUp,
        required: true,
    },

    {
        key: "inputdata_ioh",
        title: "IOH Data",
        subtitle: "Input Data",
        icon: DataUsage,
        required: true,
    },

    {
        key: "inputdata_poh",
        title: "POH Data",
        subtitle: "Input Data",
        icon: DataUsage,
        required: true,
    },

    {
        key: "enga_section_data",
        title: "EGA / ENGA Data",
        subtitle: "Section Data",
        icon: DataUsage,
        required: true,
    },

    {
        key: "freightandproformaoncost",
        title: "Store Proforma",
        subtitle: "Freight & Proforma",
        icon: Storage,
        required: true,
    },

    // ========================================================
    // OPTIONAL DATA
    // ========================================================

    {
        key: "inputdata_creditjv",
        title: "Credit JV Data",
        subtitle: "Input Data",
        icon: Storage,
        required: false,
    },

    {
        key: "inputdata_wls",
        title: "WLS Data",
        subtitle: "Input Data",
        icon: Storage,
        required: false,
    },
];


// ============================================================
// COMPONENT
// ============================================================

const IMMISDataProcess = () => {

    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);

    const [loading, setLoading] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);

    const [processing, setProcessing] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const [dataStatus, setDataStatus] = useState(null);

    const [finalised, setFinalised] = useState(false);


    // ========================================================
    // STATUS DATA
    // ========================================================

    const statusData = dataStatus || {};


    // ========================================================
    // MONTH NAME
    // ========================================================

    const getMonthName = (monthNumber) => {

        if (!monthNumber) {
            return "";
        }

        if (
            monthNames &&
            monthNames[Number(monthNumber)]
        ) {
            return monthNames[Number(monthNumber)];
        }

        const date = new Date(
            2000,
            Number(monthNumber) - 1,
            1
        );

        return date.toLocaleString(
            "en-US",
            {
                month: "long",
            }
        );
    };


    // ========================================================
    // DATA AVAILABLE
    // ========================================================

    const isAvailable = (key) => {

        const item = statusData?.[key];

        return Boolean(item?.status);
    };


    // ========================================================
    // ROW COUNT
    // ========================================================

    const getRowCount = (key) => {

        return Number(
            statusData?.[key]?.rowCount || 0
        );
    };


    // ========================================================
    // LOAD DATA STATUS
    // ========================================================

    const loadDataStatus = async (
        currentMonthValue,
        currentYearValue
    ) => {

        try {

            setStatusLoading(true);
            setError("");

            const response =
                await getDashboardDataStatus(
                    currentMonthValue,
                    currentYearValue
                );

            const status =
                response?.data?.data || {};

            setDataStatus(status);


            // =================================================
            // FINALISATION STATUS
            // =================================================

            const finalisedValue =
                status?.finalised ??
                status?.data_finalised ??
                status?.isFinalised ??
                status?.is_finalised ??
                response?.data?.finalised ??
                response?.data?.data_finalised ??
                false;

            setFinalised(
                Boolean(finalisedValue)
            );

        } catch (err) {

            console.error(
                "DATA STATUS ERROR:",
                err
            );

            setDataStatus(null);

            setError(
                err?.response?.data?.message ||
                "Unable to check Dashboard data status."
            );

        } finally {

            setStatusLoading(false);
        }
    };


    // ========================================================
    // LOAD CURRENT MONTH
    // ========================================================

    const loadCurrentMonth = async () => {

        try {

            setLoading(true);
            setError("");

            const response =
                await currentMonth();

            const data =
                response?.data?.data;

            const currentMonthValue =
                data?.month;

            const currentYearValue =
                data?.year;

            setMonth(
                currentMonthValue
            );

            setYear(
                currentYearValue
            );


            if (
                currentMonthValue &&
                currentYearValue
            ) {

                await loadDataStatus(
                    currentMonthValue,
                    currentYearValue
                );
            }

        } catch (err) {

            console.error(
                "CURRENT MONTH ERROR:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Unable to load current month."
            );

        } finally {

            setLoading(false);
        }
    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadCurrentMonth();

    }, []);


    // ========================================================
    // SUMMARY
    // ========================================================

    const summary = useMemo(() => {

        const requiredItems =
            dataItems.filter(
                (item) => item.required
            );

        let completed = 0;
        let pending = 0;
        let totalRows = 0;


        requiredItems.forEach((item) => {

            const data =
                statusData?.[item.key];

            const available =
                Boolean(data?.status);

            const rows =
                Number(data?.rowCount || 0);

            totalRows += rows;


            if (available) {

                completed++;

            } else {

                pending++;
            }

        });


        const total =
            requiredItems.length;


        const percentage =
            total > 0
                ? Math.round(
                    (completed / total) * 100
                )
                : 0;


        return {
            total,
            completed,
            pending,
            totalRows,
            percentage,
        };

    }, [statusData]);


    // ========================================================
    // OPTIONAL SUMMARY
    // ========================================================

    const optionalSummary = useMemo(() => {

        const optionalItems =
            dataItems.filter(
                (item) => !item.required
            );

        const available =
            optionalItems.filter(
                (item) =>
                    isAvailable(item.key)
            ).length;

        const rows =
            optionalItems.reduce(
                (total, item) =>
                    total +
                    getRowCount(item.key),
                0
            );

        return {
            total: optionalItems.length,
            available,
            rows,
        };

    }, [statusData]);


    // ========================================================
    // MISSING REQUIRED DATA
    // ========================================================

    const missingData =
        dataItems.filter(
            (item) =>
                item.required &&
                !isAvailable(item.key)
        );


    const allDataAvailable =
        missingData.length === 0;


    // ========================================================
    // PROCESSING OUTPUT
    // ========================================================

    const processingData =
        statusData?.labour_sub_ledger;

    const processingAvailable =
        Boolean(
            processingData?.status
        );

    const processingRows =
        Number(
            processingData?.rowCount || 0
        );


    // ========================================================
    // REFRESH
    // ========================================================

    const handleRefresh = async () => {

        try {

            setRefreshing(true);

            setMessage("");
            setError("");

            await loadCurrentMonth();

        } finally {

            setRefreshing(false);
        }
    };


    // ========================================================
    // DATA PROCESS
    // ========================================================

    const handleDataProcess = async () => {

        if (!month || !year) {

            setError(
                "Month and year are not available."
            );

            return;
        }


        if (finalised) {

            setError(
                `Data for ${getMonthName(month)} ${year} has already been finalised.`
            );

            return;
        }


        if (!allDataAvailable) {

            setError(
                "Data processing cannot start because some required data is missing."
            );

            return;
        }


        const confirmed =
            window.confirm(
                `All required data is available for ${getMonthName(month)} ${year}.\n\n` +
                `Total input rows: ${summary.totalRows.toLocaleString()}\n\n` +
                `Are you sure you want to Finalise and Process this month's data?`
            );


        if (!confirmed) {
            return;
        }


        try {

            setProcessing(true);

            setMessage("");
            setError("");


            const response =
                await dataProcess(
                    month,
                    year
                );


            setMessage(
                response?.data?.message ||
                "Data processing completed successfully."
            );


            await loadDataStatus(
                month,
                year
            );

        } catch (err) {

            console.error(
                "DATA PROCESS ERROR:",
                err
            );

            setError(
                err?.response?.data?.message ||
                "Data processing failed."
            );

        } finally {

            setProcessing(false);
        }
    };


    // ========================================================
    // STATUS CARD
    // ========================================================

    const StatusCard = ({ item }) => {

        const Icon =
            item.icon || Storage;

        const available =
            isAvailable(item.key);

        const rowCount =
            getRowCount(item.key);

        const optional =
            !item.required;


        return (

            <Card
                elevation={0}
                sx={{
                    height: "100%",
                    borderRadius: 3,
                    overflow: "hidden",

                    border: "1px solid",

                    borderColor:
                        optional
                            ? "#90caf9"
                            : available
                                ? "#a5d6a7"
                                : "#ffcdd2",

                    background:
                        optional
                            ? "linear-gradient(145deg,#f1f8ff,#ffffff)"
                            : available
                                ? "linear-gradient(145deg,#f0faf1,#ffffff)"
                                : "linear-gradient(145deg,#fff5f5,#ffffff)",

                    transition:
                        "all .25s ease",

                    "&:hover": {
                        transform:
                            "translateY(-4px)",

                        boxShadow:
                            "0 10px 26px rgba(0,0,0,.10)",
                    },
                }}
            >

                {/* TOP STATUS BAR */}

                <Box
                    sx={{
                        height: 5,

                        backgroundColor:
                            optional
                                ? "#1976d2"
                                : available
                                    ? "#2e7d32"
                                    : "#d32f2f",
                    }}
                />


                <CardContent
                    sx={{
                        p: 2,
                    }}
                >

                    {/* HEADER */}

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >

                        <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                        >

                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    backgroundColor:
                                        optional
                                            ? "#e3f2fd"
                                            : available
                                                ? "#c8e6c9"
                                                : "#ffcdd2",

                                    color:
                                        optional
                                            ? "#1565c0"
                                            : available
                                                ? "#2e7d32"
                                                : "#c62828",
                                }}
                            >

                                <Icon
                                    fontSize="small"
                                />

                            </Box>


                            <Box>

                                <Typography
                                    fontWeight={800}
                                    fontSize=".94rem"
                                    lineHeight={1.2}
                                >
                                    {item.title}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {item.subtitle}
                                </Typography>

                            </Box>

                        </Stack>


                        {/* STATUS ICON */}

                        {optional ? (

                            <Tooltip
                                title="Optional data"
                            >

                                <Warning
                                    sx={{
                                        color:
                                            "#1976d2",
                                    }}
                                />

                            </Tooltip>

                        ) : available ? (

                            <Tooltip
                                title="Required data available"
                            >

                                <CheckCircle
                                    sx={{
                                        color:
                                            "#2e7d32",
                                    }}
                                />

                            </Tooltip>

                        ) : (

                            <Tooltip
                                title="Required data missing"
                            >

                                <Cancel
                                    sx={{
                                        color:
                                            "#d32f2f",
                                    }}
                                />

                            </Tooltip>

                        )}

                    </Stack>


                    {/* ROW COUNT */}

                    <Box
                        sx={{
                            mt: 2.3,
                        }}
                    >

                        <Stack
                            direction="row"
                            alignItems="baseline"
                            spacing={0.6}
                        >

                            <Typography
                                sx={{
                                    fontSize:
                                        "1.65rem",
                                    fontWeight: 900,
                                    lineHeight: 1,
                                }}
                            >
                                {rowCount.toLocaleString()}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                rows
                            </Typography>

                        </Stack>

                    </Box>


                    {/* STATUS */}

                    <Stack
                        direction="row"
                        spacing={0.7}
                        sx={{
                            mt: 1.5,
                        }}
                    >

                        <Chip
                            size="small"
                            label={
                                optional
                                    ? "Optional"
                                    : "Required"
                            }
                            sx={{
                                fontWeight: 700,

                                backgroundColor:
                                    optional
                                        ? "#e3f2fd"
                                        : "#f5f5f5",

                                color:
                                    optional
                                        ? "#1565c0"
                                        : "#555",
                            }}
                        />


                        <Chip
                            size="small"
                            icon={
                                optional
                                    ? <Delete />
                                    : available
                                        ? <CheckCircle />
                                        : <Cancel />
                            }
                            label={
                                optional
                                    ? available
                                        ? "Available"
                                        : "Not Available"
                                    : available
                                        ? "Available"
                                        : "Missing"
                            }
                            sx={{
                                fontWeight: 700,

                                backgroundColor:
                                    optional
                                        ? available
                                            ? "#e8f5e9"
                                            : "#f5f9ff"
                                        : available
                                            ? "#e8f5e9"
                                            : "#ffebee",

                                color:
                                    optional
                                        ? available
                                            ? "#2e7d32"
                                            : "#1565c0"
                                        : available
                                            ? "#2e7d32"
                                            : "#c62828",
                            }}
                        />

                    </Stack>


                    {/* OPTIONAL MESSAGE */}

                    {optional && (

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: "block",
                                mt: 1,
                            }}
                        >
                            Not required for
                            finalisation
                        </Typography>

                    )}

                </CardContent>

            </Card>
        );
    };


    // ========================================================
    // LOADING
    // ========================================================

    if (loading) {

        return (

            <>

                <SEO
                    title="Data Processing - WGR Data Management System"
                    description="Data processing and finalisation."
                    url="https://wgr.mwsgkp.in/immis-data-process"
                />

                <BreadcrumbsBar
                    page="Data Process"
                />

                <Box
                    sx={{
                        minHeight: "60vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >

                    <Stack
                        alignItems="center"
                        spacing={2}
                    >

                        <CircularProgress
                            sx={{
                                color: "#c28a00",
                            }}
                        />

                        <Typography
                            color="text.secondary"
                            fontWeight={600}
                        >
                            Loading monthly data...
                        </Typography>

                    </Stack>

                </Box>

            </>
        );
    }


    // ========================================================
    // RENDER
    // ========================================================

    return (

        <>

            <SEO
                title="Data Processing - WGR Data Management System"
                description="Data processing and finalisation."
                url="https://wgr.mwsgkp.in/immis-data-process"
            />

{/* 
            <BreadcrumbsBar
                page="Data Process"
            /> */}


             <Box
      sx={{
        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >

                {/* ==================================================
                    PROFESSIONAL HEADER
                ================================================== */}

                <PageHeader
        title="Data Processing"
        subtitle={`Data entry status for the month of: ${getMonthName(month)} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
        showRefresh
        onRefresh={handleRefresh}
         refreshing={loading}
      />




      


                {/* ==================================================
                    ERROR
                ================================================== */}

                {error && (

                    <Alert
                        severity="error"
                        sx={{
                            mb: 2.5,
                            borderRadius: 2,
                        }}
                    >
                        {error}
                    </Alert>

                )}


                {/* ==================================================
                    SUMMARY CARDS
                ================================================== */}

                <Grid
                    container
                    spacing={2}
                    sx={{
                        mb: 2.5,
                    }}
                >

                    <Grid
                        size={{
                            xs: 6,
                            sm: 3,
                        }}
                    >

                        <SummaryCard
                            title="Required Sources"
                            value={summary.total}
                            subtitle="Must be available"
                            icon={
                                <Storage />
                            }
                        />

                    </Grid>


                    <Grid
                        size={{
                            xs: 6,
                            sm: 3,
                        }}
                    >

                        <SummaryCard
                            title="Available"
                            value={summary.completed}
                            subtitle="Required sources"
                            icon={
                                <CheckCircle />
                            }
                            success
                        />

                    </Grid>


                    <Grid
                        size={{
                            xs: 6,
                            sm: 3,
                        }}
                    >

                        <SummaryCard
                            title="Missing"
                            value={summary.pending}
                            subtitle="Required sources"
                            icon={
                                <Cancel />
                            }
                            danger
                        />

                    </Grid>


                    <Grid
                        size={{
                            xs: 6,
                            sm: 3,
                        }}
                    >

                        <SummaryCard
                            title="Input Rows"
                            value={
                                summary.totalRows.toLocaleString()
                            }
                            subtitle="Required data rows"
                            icon={
                                <DataUsage />
                            }
                            blue
                        />

                    </Grid>

                </Grid>


                {/* ==================================================
                    OPTIONAL SUMMARY
                ================================================== */}

                <Paper
                    elevation={0}
                    sx={{
                        mb: 2.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: 2.5,
                        border:
                            "1px solid #bbdefb",
                        background:
                            "#f5faff",
                    }}
                >

                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                    >

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                        >

                            <Warning
                                sx={{
                                    color:
                                        "#1976d2",
                                    fontSize: 21,
                                }}
                            />

                            <Typography
                                variant="body2"
                                fontWeight={700}
                            >
                                Optional Data
                            </Typography>

                            <Chip
                                size="small"
                                label={`${optionalSummary.total} optional sources`}
                                sx={{
                                    fontWeight: 600,
                                    color:
                                        "#1565c0",
                                    background:
                                        "#e3f2fd",
                                }}
                            />

                        </Stack>


                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            WLS and Credit JV are optional
                            and do not block finalisation.
                        </Typography>

                    </Stack>

                </Paper>


                {/* ==================================================
                    READINESS
                ================================================== */}

                <Card
                    elevation={0}
                    sx={{
                        mb: 2.5,
                        borderRadius: 3,
                        border:
                            "1px solid #e0e0e0",
                        background:
                            "#ffffff",
                    }}
                >

                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 2.5,
                            },
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                        >

                            <Box>

                                <Typography
                                    fontWeight={900}
                                    fontSize="1.05rem"
                                >
                                    Data Readiness
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {summary.completed} of{" "}
                                    {summary.total} required
                                    sources available
                                </Typography>

                            </Box>


                            <Chip
                                label={`${summary.percentage}% Ready`}
                                color={
                                    allDataAvailable
                                        ? "success"
                                        : "warning"
                                }
                                sx={{
                                    fontWeight: 800,
                                }}
                            />

                        </Stack>


                        <Box
                            sx={{
                                mt: 2,
                                height: 13,
                                borderRadius: 10,
                                overflow: "hidden",
                                backgroundColor:
                                    "#eeeeee",
                            }}
                        >

                            <Box
                                sx={{
                                    width:
                                        `${summary.percentage}%`,
                                    height: "100%",
                                    borderRadius: 10,

                                    background:
                                        allDataAvailable
                                            ? "linear-gradient(90deg,#2e7d32,#66bb6a)"
                                            : "linear-gradient(90deg,#c28a00,#e0ad35)",

                                    transition:
                                        "width .5s ease",
                                }}
                            />

                        </Box>

                    </CardContent>

                </Card>


                {/* ==================================================
                    FINALISATION STATUS
                ================================================== */}

                <Alert
                    severity={
                        finalised
                            ? "warning"
                            : allDataAvailable
                                ? "success"
                                : "warning"
                    }
                    icon={
                        finalised
                            ? <Lock />
                            : allDataAvailable
                                ? <CheckCircle />
                                : <Warning />
                    }
                    sx={{
                        mb: 2.5,
                        borderRadius: 2.5,
                    }}
                >

                    <Typography
                        fontWeight={800}
                    >

                        {finalised

                            ? `Data for ${getMonthName(month)} ${year} has already been finalised.`

                            : allDataAvailable

                                ? "All required data is available. This month is ready for finalisation."

                                : `${summary.pending} required data source(s) are missing.`

                        }

                    </Typography>


                    {!finalised &&
                        !allDataAvailable && (

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: .4,
                                }}
                            >
                                Complete all required
                                missing data before
                                processing.
                            </Typography>

                        )}

                </Alert>


                {/* ==================================================
                    INPUT DATA
                ================================================== */}

                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border:
                            "1px solid #dfe3e8",
                        overflow: "hidden",
                    }}
                >

                    {/* SECTION HEADER */}

                    <Box
                        sx={{
                            px: {
                                xs: 2,
                                sm: 2.5,
                            },
                            py: 2,

                            background:
                                "linear-gradient(90deg,#fffaf0,#ffffff)",

                            borderBottom:
                                "1px solid #ececec",
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                        >

                            <Box>

                                <Typography
                                    fontWeight={900}
                                    color="#8a6200"
                                    fontSize="1.1rem"
                                >
                                    Input Data Status
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Review required and optional
                                    sources before processing.
                                </Typography>

                            </Box>


                            <Stack
                                direction="row"
                                spacing={1}
                            >

                                <Chip
                                    size="small"
                                    label={`Required: ${summary.total}`}
                                    variant="outlined"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={`Optional: ${optionalSummary.total}`}
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                />

                            </Stack>

                        </Stack>

                    </Box>


                    {/* DATA CARDS */}

                    <Box
                        sx={{
                            p: {
                                xs: 1.5,
                                sm: 2.5,
                            },
                        }}
                    >

                        {statusLoading ? (

                            <Box
                                sx={{
                                    py: 7,
                                    textAlign: "center",
                                }}
                            >

                                <CircularProgress
                                    sx={{
                                        color:
                                            "#c28a00",
                                    }}
                                />

                                <Typography
                                    sx={{
                                        mt: 1.5,
                                    }}
                                    color="text.secondary"
                                >
                                    Checking data status...
                                </Typography>

                            </Box>

                        ) : (

                            <Grid
                                container
                                spacing={2}
                            >

                                {dataItems.map(
                                    (item) => (

                                        <Grid
                                            key={
                                                item.key
                                            }
                                            size={{
                                                xs: 12,
                                                sm: 6,
                                                md: 4,
                                                lg: 3,
                                            }}
                                        >

                                            <StatusCard
                                                item={
                                                    item
                                                }
                                            />

                                        </Grid>

                                    )
                                )}

                            </Grid>

                        )}

                    </Box>

                </Card>


                {/* ==================================================
                    MISSING REQUIRED DATA
                ================================================== */}

                {!allDataAvailable &&
                    !finalised && (

                        <Alert
                            severity="error"
                            sx={{
                                mt: 2.5,
                                borderRadius: 2.5,
                            }}
                        >

                            <Typography
                                fontWeight={800}
                            >
                                Processing is currently
                                blocked.
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: .5,
                                }}
                            >
                                Missing required data:
                            </Typography>


                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={0.8}
                                sx={{
                                    mt: 1,
                                }}
                            >

                                {missingData.map(
                                    (item) => (

                                        <Chip
                                            key={
                                                item.key
                                            }
                                            size="small"
                                            label={
                                                item.title
                                            }
                                            color="error"
                                            variant="outlined"
                                        />

                                    )
                                )}

                            </Stack>

                        </Alert>

                    )}


                {/* ==================================================
                    SUCCESS
                ================================================== */}

                {message && (

                    <Alert
                        severity="success"
                        icon={
                            <CheckCircle />
                        }
                        sx={{
                            mt: 2.5,
                            borderRadius: 2.5,
                        }}
                    >
                        {message}
                    </Alert>

                )}


                {/* ==================================================
                    PROCESS BUTTON
                ================================================== */}

                <Card
                    elevation={0}
                    sx={{
                        mt: 2.5,
                        borderRadius: 3,

                        border:
                            finalised
                                ? "1px solid #ffcc80"
                                : allDataAvailable
                                    ? "1px solid #d6b25e"
                                    : "1px solid #e0e0e0",

                        background:
                            finalised
                                ? "#fffaf0"
                                : allDataAvailable
                                    ? "linear-gradient(135deg,#fffaf0,#ffffff)"
                                    : "#fafafa",
                    }}
                >

                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 2.5,
                            },
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column",
                                md: "row",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "stretch",
                                md: "center",
                            }}
                        >

                            <Box>

                                <Typography
                                    fontWeight={900}
                                >
                                    Finalise & Process
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: .4,
                                    }}
                                >

                                    {finalised

                                        ? "This month's data has already been finalised."

                                        : allDataAvailable

                                            ? "All required data is ready for final processing."

                                            : "Complete all required data sources before processing."
                                    }

                                </Typography>

                            </Box>


                            <Button
                                variant="contained"
                                size="large"
                                startIcon={
                                    processing
                                        ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        )
                                        : finalised
                                            ? <Lock />
                                            : <PlayArrow />
                                }
                                onClick={
                                    handleDataProcess
                                }
                                disabled={
                                    processing ||
                                    statusLoading ||
                                    !month ||
                                    !year ||
                                    finalised ||
                                    !allDataAvailable
                                }
                                sx={{
                                    minWidth: {
                                        xs: "100%",
                                        md: 260,
                                    },

                                    py: 1.5,

                                    borderRadius: 2.5,

                                    fontWeight: 900,

                                    textTransform:
                                        "none",

                                    backgroundColor:
                                        "#c28a00",

                                    "&:hover": {
                                        backgroundColor:
                                            "#a87500",
                                    },

                                    "&:disabled": {
                                        backgroundColor:
                                            "#d7c79e",
                                        color:
                                            "#ffffff",
                                    },
                                }}
                            >

                                {processing

                                    ? "Finalising & Processing..."

                                    : finalised

                                        ? "Data Already Finalised"

                                        : !allDataAvailable

                                            ? "Data Not Complete"

                                            : "Finalise & Process Data"

                                }

                            </Button>

                        </Stack>

                    </CardContent>

                </Card>


                {/* ==================================================
                    PROCESSING RESULT
                ================================================== */}

                <Card
                    elevation={0}
                    sx={{
                        mt: 2.5,
                        borderRadius: 3,

                        border:
                            processingAvailable
                                ? "1px solid #a5d6a7"
                                : "1px solid #e0e0e0",

                        background:
                            processingAvailable
                                ? "#f1f8f3"
                                : "#fafafa",
                    }}
                >

                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 2.5,
                            },
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                        >

                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                            >

                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        backgroundColor:
                                            processingAvailable
                                                ? "#c8e6c9"
                                                : "#eeeeee",

                                        color:
                                            processingAvailable
                                                ? "#2e7d32"
                                                : "#757575",
                                    }}
                                >

                                    <PlayArrow />

                                </Box>


                                <Box>

                                    <Typography
                                        fontWeight={900}
                                    >
                                        Data Processing Status
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Labour Sub Ledger
                                    </Typography>

                                </Box>

                            </Stack>


                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >

                                <Box
                                    sx={{
                                        textAlign: {
                                            xs: "left",
                                            sm: "right",
                                        },
                                    }}
                                >

                                    <Typography
                                        variant="h5"
                                        fontWeight={900}
                                    >
                                        {processingRows.toLocaleString()}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        processed rows
                                    </Typography>

                                </Box>


                                <Chip
                                    icon={
                                        processingAvailable
                                            ? <CheckCircle />
                                            : <Cancel />
                                    }
                                    label={
                                        processingAvailable
                                            ? "Processed"
                                            : "Not Processed"
                                    }
                                    sx={{
                                        fontWeight: 800,

                                        backgroundColor:
                                            processingAvailable
                                                ? "#c8e6c9"
                                                : "#eeeeee",

                                        color:
                                            processingAvailable
                                                ? "#2e7d32"
                                                : "#757575",
                                    }}
                                />

                            </Stack>

                        </Stack>

                    </CardContent>

                </Card>


                {/* FOOTER NOTE */}

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        textAlign: "center",
                        mt: 2,
                        mb: 1,
                    }}
                >
                    Processing is allowed only when all
                    required input data is available and
                    the month has not been finalised.
                    Optional WLS and Credit JV data do
                    not block processing.
                </Typography>

            </Box>

        </>
    );
};


// ============================================================
// SUMMARY CARD
// ============================================================

function SummaryCard({
    title,
    value,
    subtitle,
    icon,
    success,
    danger,
    blue,
}) {

    const iconColor =
        success
            ? "#2e7d32"
            : danger
                ? "#c62828"
                : blue
                    ? "#1565c0"
                    : "#6d5200";


    const iconBackground =
        success
            ? "#c8e6c9"
            : danger
                ? "#ffcdd2"
                : blue
                    ? "#bbdefb"
                    : "#fff0c2";


    return (

        <Paper
            elevation={0}
            sx={{
                height: "100%",
                p: 2,

                borderRadius: 3,

                border:
                    "1px solid #e1e5ea",

                backgroundColor:
                    "#ffffff",

                transition:
                    "all .2s ease",

                "&:hover": {
                    transform:
                        "translateY(-3px)",

                    boxShadow:
                        "0 8px 20px rgba(0,0,0,.08)",
                },
            }}
        >

            <Stack
                direction="row"
                spacing={1.3}
                alignItems="center"
            >

                <Box
                    sx={{
                        width: 45,
                        height: 45,
                        borderRadius: 2,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        backgroundColor:
                            iconBackground,

                        color:
                            iconColor,

                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>


                <Box
                    sx={{
                        minWidth: 0,
                    }}
                >

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                        noWrap
                    >
                        {title}
                    </Typography>


                    <Typography
                        variant="h4"
                        fontWeight={900}
                        sx={{
                            lineHeight: 1.1,
                            color:
                                iconColor,
                        }}
                    >
                        {value}
                    </Typography>


                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {subtitle}
                    </Typography>

                </Box>

            </Stack>

        </Paper>
    );
};


export default IMMISDataProcess;

